import { useState, useEffect, useCallback } from 'react';
// 2026-09-24 (Jaime ruling): create-your-unit nudge for home-less users.
import CreateHomeNudge from '../components/CreateHomeNudge';
import { track } from '../services/analyticsTracker';
import { useActiveHomeUnitId } from '../hooks/useActiveHomeUnitId';
import { Plus, Trash2, X, TrendingUp, TrendingDown, Wallet, PiggyBank, Loader2, AlertCircle, CreditCard, RefreshCw } from 'lucide-react';
import { UnitPrompt } from '../components/HomeStateBanners';
import { homeApi } from '../services/homeApi';
import { pageErrorFromApi } from '../services/planLimitModal';

interface Transaction {
  id: string;
  type: 'income' | 'expense';
  category: string;
  amount: number | string;
  description: string;
  date: string;
}

const emptyForm = {
  type: 'expense' as 'income' | 'expense',
  category: '',
  amount: '',
  description: '',
  date: new Date().toISOString().split('T')[0],
};

const categoryIcons: Record<string, string> = {
  Comida: '🍽️',
  Transporte: '🚗',
  Servicios: '💡',
  Entretenimiento: '🎬',
  Salud: '💊',
  Educación: '📚',
  Ingresos: '💰',
  Otro: '📦',
};

// Safely convert any amount value (string, number, null, undefined) to a number.
const toNum = (val: any): number => {
  const n = typeof val === 'number' ? val : parseFloat(String(val));
  return isNaN(n) ? 0 : n;
};

const fmtMoney = (n: number): string => {
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function HomeBudget() {
  // FIX (2026-08-31, Jaime bug): explicit active-unit scoping + refetch on unit change.
  const activeUnitId = useActiveHomeUnitId();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchTransactions = useCallback(async () => {
    // Jaime 2026-09-30: never fire a unitId-dependent request without a
    // resolved unit — show the unit-picker prompt state instead.
    if (!activeUnitId) { setTransactions([]); setError(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await homeApi.budget.list(activeUnitId);
      const payload = res.data?.data ?? res.data;
      const entries = payload?.entries || payload?.data?.entries || (Array.isArray(payload) ? payload : []);
      setTransactions(Array.isArray(entries) ? entries : []);
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al cargar presupuesto'));
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [activeUnitId]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const handleSubmit = async () => {
    if (!form.amount || !form.category.trim()) return;
    try {
      await homeApi.budget.create({
        ...form,
        amount: parseFloat(form.amount),
      }, activeUnitId);
      track('budget_entry_created');
      setShowForm(false);
      setForm(emptyForm);
      fetchTransactions();
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al guardar transacción'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar esta transacción?')) return;
    try {
      // FIX (2026-09-28, ticket #216): same missing-unitId pattern as shopping delete.
      await homeApi.budget.delete(id, activeUnitId);
      fetchTransactions();
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al eliminar transacción'));
    }
  };

  // Use toNum() to ensure numeric addition — API may return amounts as strings.
  const totalIncome = transactions.filter(t => t.type === 'income').reduce((acc, t) => acc + toNum(t.amount), 0);
  const totalExpenses = transactions.filter(t => t.type === 'expense').reduce((acc, t) => acc + toNum(t.amount), 0);
  const balance = totalIncome - totalExpenses;

  // Category breakdown for expenses
  const expenseByCategory: Record<string, number> = {};
  transactions.filter(t => t.type === 'expense').forEach(t => {
    const cat = t.category || 'Otro';
    expenseByCategory[cat] = (expenseByCategory[cat] || 0) + toNum(t.amount);
  });
  const categoryBreakdown = Object.entries(expenseByCategory).sort((a, b) => b[1] - a[1]);
  const maxCategoryAmount = categoryBreakdown.length > 0 ? categoryBreakdown[0][1] : 0;

  // Sort transactions by date desc
  const sortedTransactions = [...transactions].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return db - da;
  });

  return (
    <div className="space-y-6">
      <CreateHomeNudge />
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-green-600 to-green-700 p-4 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="w-5 h-5 text-green-100" />
            <span className="text-green-100 text-sm">Ingresos</span>
          </div>
          <p className="text-2xl font-bold text-white">{fmtMoney(totalIncome)}</p>
        </div>
        <div className="bg-gradient-to-br from-red-600 to-red-700 p-4 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-5 h-5 text-red-100" />
            <span className="text-red-100 text-sm">Gastos</span>
          </div>
          <p className="text-2xl font-bold text-white">{fmtMoney(totalExpenses)}</p>
        </div>
        <div className={`bg-gradient-to-br p-4 rounded-xl ${balance >= 0 ? 'from-blue-600 to-blue-700' : 'from-orange-600 to-orange-700'}`}>
          <div className="flex items-center gap-2 mb-2">
            <PiggyBank className="w-5 h-5 text-blue-100" />
            <span className="text-blue-100 text-sm">Balance</span>
          </div>
          <p className="text-2xl font-bold text-white">{fmtMoney(balance)}</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-500/50 text-red-700 dark:text-red-300 p-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => { setError(null); void fetchTransactions(); }} className="ml-auto flex items-center gap-1 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"><RefreshCw className="w-4 h-4" />Reintentar</button>
          <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"><X className="w-4 h-4" /></button>
        </div>
      )}

      {!activeUnitId && !loading && <UnitPrompt />}

      {/* Add Button */}
      <button
        onClick={() => { setForm(emptyForm); setShowForm(true); }}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
      >
        <Plus className="w-5 h-5" />
        Registrar Transacción
      </button>

      {/* Form */}
      {showForm && (
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[var(--v-text-primary)] dark:text-white font-semibold">Nueva Transacción</h3>
            <button onClick={() => setShowForm(false)} className="text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select
              value={form.type}
              onChange={e => setForm({ ...form, type: e.target.value as 'income' | 'expense' })}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="expense">Gasto</option>
              <option value="income">Ingreso</option>
            </select>
            <input
              type="number"
              value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              placeholder="Monto"
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            />
            <input
              type="text"
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              placeholder="Categoría"
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            />
            <input
              type="date"
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <input
            type="text"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="Descripción"
            className="w-full bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
          />
          <button
            onClick={handleSubmit}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium"
          >
            Guardar
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500 dark:text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Cargando presupuesto...
        </div>
      )}

      {/* Category Breakdown */}
      {!loading && categoryBreakdown.length > 0 && (
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <h3 className="font-semibold text-[var(--v-text-primary)] dark:text-white mb-3">Desglose por Categoría</h3>
          <div className="space-y-3">
            {categoryBreakdown.map(([cat, amount]) => (
              <div key={cat}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-700 dark:text-slate-300 text-sm">
                    {categoryIcons[cat] || '📦'} {cat}
                  </span>
                  <span className="text-[var(--v-text-primary)] dark:text-white text-sm font-medium">{fmtMoney(amount)}</span>
                </div>
                <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-blue-500"
                    style={{ width: `${maxCategoryAmount > 0 ? (amount / maxCategoryAmount) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transaction List */}
      {!loading && !error && activeUnitId && (
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
          <div className="p-4 border-b border-[var(--v-surface-3)]">
            <h3 className="font-semibold text-[var(--v-text-primary)] dark:text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Transacciones
            </h3>
          </div>
          {sortedTransactions.length === 0 ? (
            <div className="text-center py-12 text-gray-400 dark:text-slate-500">
              <p>No hay transacciones registradas</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-slate-700">
              {sortedTransactions.map((t) => (
                <div key={t.id} className="p-4 flex items-center justify-between group">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      t.type === 'income' ? 'bg-green-100 dark:bg-green-500/20' : 'bg-red-100 dark:bg-red-500/20'
                    }`}>
                      {t.type === 'income' ? (
                        <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[var(--v-text-primary)] dark:text-white font-medium truncate">{t.description || 'Sin descripción'}</p>
                      <p className="text-gray-400 dark:text-slate-500 text-xs">
                        {t.category || '—'} • {t.date ? new Date(t.date).toLocaleDateString() : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <p className={`font-semibold ${t.type === 'income' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {t.type === 'income' ? '+' : '-'}{fmtMoney(toNum(t.amount))}
                    </p>
                    <button
                      onClick={() => handleDelete(t.id)}
                      className="text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-600 dark:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
